import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { Title, Meta } from '@angular/platform-browser';

import {
    Collection,
    StoreSite,
    HeroSectionData,
    Page,
    WebPresenceConfigData,
    GetProductPageQuery,
} from '../../../common/generated-types';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { StateService } from '../../../core/providers/state/state.service';
import { ActiveService } from '../../../core/providers/active/active.service';
import { NGXLogger } from 'ngx-logger';
import { parseMaybeJsonList, parseMaybeJsonValue } from '../../../common/utils/safe-json-parser';
import { LayoutOptionData, NavMenuOption, ProductCustomizationOption, SiteStyleData } from '../../../common/interfaces';
import { stripHtml } from '../../../common/utils/strip-html';
import { replaceHostname } from '../../../common/utils/url-handlers';
import { getRoundedClass, computeLayoutStyles } from '../../../common/utils/styles-helper';
import { CanonicalService } from '../../../core/providers/canonical/canonical.service';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { DESKTOP_HEADER_HEIGHT, DESKTOP_TOP_MESSAGE_HEIGHT, DESKTOP_SECTION_NAV_HEIGHT, WAIT_FOR_NAVIGATION_INTERVAL, WAIT_FOR_NAVIGATION_TIMEOUT, DESKTOP_HEADER_HEIGHT_WITH_TOP_MESSAGE } from '../../../common/constants';
import { CarouselArrowType, MobileBuyBannerType, SiteStyleType } from '../../../common/enums';
import { StructuredDataService } from '../../../core/providers/structured-data/structured-data.service';
import { AssetPreviewPipe } from '../../../shared/pipes/asset-preview.pipe';
import { navigateByNavigationItem } from '../../../common/utils/navigation-helpers';

type Variant = NonNullable<GetProductPageQuery['productPage']['productInfo']>['variants'][number];

declare let document: Document & typeof globalThis;

@Component({
    selector: 'vsf-product-page',
    templateUrl: './product-page.component.html',
    styleUrls: ['./product-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductPageComponent implements OnInit, OnDestroy {

    productPage: Page;
    product: any;
    qtyInCart: { [id: string]: number; } = {};
    selectedVariant: Variant;
    qty = 1;
    breadcrumbs: Collection['breadcrumbs'] | null = null;
    inFlight = false;
    inStock = true;
    @ViewChild('addedToCartTemplate', {static: true})
    private sub: Subscription;
    private orderSub: Subscription;
    private stateSub: Subscription;
    private routeParamsSub?: Subscription;
    selectedVariantMSRP: number | null = null;
    selectedPrintTemplate: ProductCustomizationOption | null = null;
    discountPercentage: number | null = null;
    isMobile: boolean | null = null;
    baseUrl = '';
    shopHost: string;
    currentCanonicalURL: string | null = null;
    heroSection: HeroSectionData | null = null;
    storeInfo: StoreSite | null = null;
    sellerChannelId: string;
    withHeaderMessage: boolean = true;
    previewPipe = new AssetPreviewPipe();
    layoutOrder: LayoutOptionData[] = [];
    navigationMenu: NavMenuOption[] = [];
    webPresenceConfig: WebPresenceConfigData | null = null;
    pagecomponentList: any[] = [];
    pageComponentIndex: number[] = [];
    h1PageHeadingIndex: number = -1;
    h1CustomText: string = '';
    h1StyleMode: string = 'auto';
    h1CustomStyles: { [key: string]: string } = {};
    showFixedBanner: boolean = false;
    isHeaderFloating: boolean = false;
    showTopNav: boolean = true;
    shopNowBannerTopOffset: number = 0;
    desktopHeaderHeight = DESKTOP_HEADER_HEIGHT;
    desktopTopMessageHeight = DESKTOP_TOP_MESSAGE_HEIGHT;
    desktopHeaderWithTopMessageHeight = DESKTOP_HEADER_HEIGHT_WITH_TOP_MESSAGE;
    desktopSectionNavHeight = DESKTOP_SECTION_NAV_HEIGHT;
    waitForNavigationInterval = WAIT_FOR_NAVIGATION_INTERVAL;
    waitForNavigationTimeout = WAIT_FOR_NAVIGATION_TIMEOUT;
    pdpAssetsArrowType: CarouselArrowType = CarouselArrowType.LargeChevron;
    mobileBuyBannerType: MobileBuyBannerType = MobileBuyBannerType.Regular;
    allCustomizationOptions: ProductCustomizationOption[] = [];
    messageValues: { [optionId: string]: string } = {};
    selectedPaidOptionValues: { [optionId: string]: string } = {};
    constructor(@Inject(PLATFORM_ID) private platformId: object,
                private stateService: StateService,
                private activeService: ActiveService,
                private canonicalService: CanonicalService,
                private route: ActivatedRoute,
                private router: Router,
                private titleService: Title,
                private metaService: Meta,
                private structuredDataService: StructuredDataService,
                private changeDetector: ChangeDetectorRef,
                private viewportScroller: ViewportScroller,
                private logger: NGXLogger) {
    }
    async ngOnInit() {
        const isMobile$ = this.stateService.select(state => state.isMobile);
        const storeInfo$ = this.stateService.select(state => state.storeInfo);
        const storePath$ = this.stateService.select(state => state.storePath);
        const shopHost$ = this.stateService.select(state => state.shopHost);
        const hasTopMessage$ = this.stateService.select(state => state.hasTopMessage);
        await combineLatest([isMobile$, storeInfo$, storePath$, shopHost$, hasTopMessage$])
        .pipe(take(1)).subscribe(([isMobile, storeInfo, storePath, shopHost, hasTopMessage]) => {
            this.isMobile = isMobile;
            this.storeInfo = storeInfo;
            this.sellerChannelId = this.storeInfo?.sellerChannel?.id || '';
            this.baseUrl = storePath;
            this.shopHost = shopHost;
            if(this.withHeaderMessage !== hasTopMessage) {
                this.withHeaderMessage = hasTopMessage;
                this.updateShopNowBannerTopOffset()
            }
            const siteStyles = parseMaybeJsonValue<SiteStyleData>(this.storeInfo?.siteStyles, this.logger);
            this.pdpAssetsArrowType = siteStyles?.pdpAssetsArrowType || CarouselArrowType.LargeChevron;
            this.mobileBuyBannerType = siteStyles?.mobileBuyBannerType || MobileBuyBannerType.Regular;
        });
        this.stateSub = this.stateService.select(state => ({
            isHeaderFloating: state.isHeaderFloating,
          })).subscribe(({ isHeaderFloating }) => {
            if(isHeaderFloating !== this.isHeaderFloating) {
                this.isHeaderFloating = isHeaderFloating;
                this.updateShopNowBannerTopOffset()
            }
        });

        const productSlug$ = this.route.paramMap.pipe(
            map(paramMap => paramMap.get('slug')),
            filter(notNullOrUndefined),
            distinctUntilChanged()
        );
        const variantId$ = this.route.queryParamMap.pipe(
            map(params => params.get('variantid')),
            distinctUntilChanged()
        );

        this.sub = combineLatest([productSlug$, variantId$]).subscribe(([ _, variantId]) => {
            this.productPage = this.route.snapshot.data['productPage'];
            this.product = this.route.snapshot.data['productPage']?.productInfo;
            let defaultVariant = this.product.variants[0];
            if(this.product?.customFields?.defaultVariant) {
                defaultVariant = this.product?.variants?.find((v:any) => v.id === this.product?.customFields?.defaultVariant?.id) || defaultVariant;
            }
            if (variantId) {
                const variant = this.product?.variants.find((v:any) => v.id === variantId);
                if (variant) {
                    this.selectedVariant = variant;
                } else {
                    this.selectedVariant = defaultVariant;
                }
            } else {
                this.selectedVariant = defaultVariant;
            }
            const selectedId = this.selectedVariant.id;
            if (!variantId) {
                // Arrived without variantid: replace current history entry so Back goes to listing
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { variantid: selectedId },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
                return;
            } else if (variantId !== selectedId) {
                // URL had a different id (rare): fix it but keep history (user action)
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { variantid: selectedId },
                    queryParamsHandling: 'merge',
                    replaceUrl: false,
                });
            }

            this.pagecomponentList = this.productPage?.pageComponentList || [];
            this.layoutOrder = this.pagecomponentList.map((pc:any) =>{
                const layoutOption =  { ...pc.layoutOption } as LayoutOptionData;
                if (layoutOption) {
                  // Compute desktop width percentage
                  layoutOption.widthPercentage = layoutOption.width && layoutOption.width > 0
                    ? `${layoutOption.width * 100}%`
                    : '100%';
                  // Compute mobile width percentage
                  layoutOption.mobileWidthPercentage = layoutOption.mobileWidth && layoutOption.mobileWidth > 0
                    ? `${layoutOption.mobileWidth * 100}%`
                    : '100%';
                  // Ensure margin properties are numbers (fallback to 0 if undefined)
                  layoutOption.topMargin = layoutOption.topMargin || 0;
                  layoutOption.bottomMargin = layoutOption.bottomMargin || 0;
                  layoutOption.mobileTopMargin = layoutOption.mobileTopMargin || 0;
                  layoutOption.mobileBottomMargin = layoutOption.mobileBottomMargin || 0;
                  layoutOption.horizontalPadding = layoutOption.horizontalPadding || 0;
                  layoutOption.mobileHorizontalPadding = layoutOption.mobileHorizontalPadding || 0;
                  layoutOption.topPadding = layoutOption.topPadding || 0;
                  layoutOption.bottomPadding = layoutOption.bottomPadding || 0;
                  layoutOption.mobileTopPadding = layoutOption.mobileTopPadding || 0;
                  layoutOption.mobileBottomPadding = layoutOption.mobileBottomPadding || 0;
                  if (this.isMobile) {
                    layoutOption.linePadding = `${layoutOption.mobileTopPadding}px ${layoutOption.mobileHorizontalPadding}px
                        ${layoutOption.mobileBottomPadding}px`;
                  } else {
                    layoutOption.linePadding = `${layoutOption.topPadding}px ${layoutOption.horizontalPadding}px
                        ${layoutOption.bottomPadding}px`;
                  }
                  const activeRounded = this.isMobile
                    ? (layoutOption.mobileSectionRoundedCorners ?? layoutOption.sectionRoundedCorners)
                    : layoutOption.sectionRoundedCorners;
                  layoutOption.roundedClass = getRoundedClass(activeRounded);
                }
                return layoutOption;
            })
            .filter(notNullOrUndefined);
            this.navigationMenu = parseMaybeJsonList<NavMenuOption>(this.productPage?.navigationMenu, this.logger);

            this.webPresenceConfig = this.productPage?.webPresence || null;
            this.computeLayoutStyles();
            this.computeH1Heading();
            if(!variantId) {
                this.selectedVariant = defaultVariant;
            }
            this.inStock = (this.selectedVariant.stockLevel !== 'OUT_OF_STOCK');
            this.updateMSRP();
            if (isPlatformBrowser(this.platformId) && variantId) {
                this.viewportScroller.scrollToPosition([0, 0]);
            }
            const canonicalURL = `/dp/${this.product?.slug.toLowerCase()}?variantid=${this.selectedVariant?.id}`;
            this.canonicalService.setCanonicalURL(`${this.shopHost}${canonicalURL}`);
            this.currentCanonicalURL = canonicalURL;
            this.updateProductMetadata(this.product, this.selectedVariant.id);
            this.structuredDataService.replaceOne('product', this.buildProductJsonLd());
            this.changeDetector.detectChanges();
            if (isPlatformBrowser(this.platformId)) {
                const section = this.route.snapshot.queryParamMap.get('section');
                if (section) {
                    this.scrollToSectionAfterLoad(section);
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { section: null },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }
            }
        });

        this.orderSub = this.activeService.activeOrder$.subscribe(order => {
            this.qtyInCart = {};
            for (const line of order?.lines ?? []) {
                this.qtyInCart[line.productVariant.id] = line.quantity;
            }
        });

        if(isPlatformBrowser(this.platformId)) {
            window.addEventListener('scroll', this.onScroll);
        }
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        if (this.orderSub) {
            this.orderSub.unsubscribe();
        }
        if (this.stateSub) {
            this.stateSub.unsubscribe();
        }
        if (this.routeParamsSub) {
            this.routeParamsSub.unsubscribe();
        }
        this.removeMetadata();
        this.structuredDataService.remove('product');
        if (this.currentCanonicalURL) {
            this.canonicalService.removeCanonicalURL();
            this.currentCanonicalURL = null;
        }
        if (isPlatformBrowser(this.platformId)) {
            window.removeEventListener('scroll', this.onScroll);
        }
    }

    private onScroll = () => {
        const scrolled = window.scrollY || document.documentElement.scrollTop;
        let showTopNav = true;
        // If scrolled beyond a certain point, hide top nav => show floating nav
        if (scrolled > 150) {
            showTopNav = false;
        }
        if(showTopNav !== this.showTopNav) {
            this.showTopNav = showTopNav;
            this.updateShopNowBannerTopOffset()
        }
    }
    scrollToSection(item: NavMenuOption): void {
        navigateByNavigationItem(this.router, item, '/');
    }

    scrollToSectionAfterLoad(sectionId: string): void {
        const interval = setInterval(() => {
          const targetEl = document.getElementById(sectionId);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth' });
            clearInterval(interval);
          }
        }, this.waitForNavigationInterval);
        setTimeout(() => clearInterval(interval), this.waitForNavigationTimeout);
    }

    private buildProductJsonLd(): object {
        let aggregateRating: any = null;
        let reviews: any = null;
        if(this.product?.customFields?.reviewRating && this.product?.customFields?.reviewCount > 0) {
            aggregateRating = {
                "@type": "AggregateRating",
                "ratingValue": this.product.customFields.reviewRating.toFixed(2),
                "reviewCount": this.product.customFields.reviewCount.toString()
            }
        }
        if(this.product?.customFields?.featuredReview) {
            reviews = [{
                "@type": "Review",
                "author": this.product.customFields.featuredReview.authorName || 'Anonymous',
                "datePublished": this.product.customFields.featuredReview.createdAt.split('T')[0] || this.product.createdAt.split('T')[0],
                "reviewBody": this.product.customFields.featuredReview.body || '',
                "name": this.product.customFields.featuredReview.summary || 'Featured Review',
                "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": this.product.customFields.featuredReview.rating.toFixed(2),
                    "bestRating": "5",
                    "worstRating": "1",
                },
            }]
        }
        const data = {
            "@context": "https://schema.org",
            "@type": "Product",
            name: this.selectedVariant.name || this.product.name || this.product.slug,
            url: `${this.storeInfo?.storeUrl}/dp/${this.product?.slug}?variantid=${this.selectedVariant?.id}`,
            image: `${this.storeInfo?.storeUrl}${this.previewPipe.transform(
                this.selectedVariant?.featuredAsset || this.product.featuredAsset || this.product.assets?.[0],
                'full',
                'jpeg'
            )}`,
            color: this.selectedVariant.options?.find((o:any) => o.group.code.toLowerCase() === 'color')?.name || undefined,
            size: this.selectedVariant.options?.find((o:any) => o.group.code.toLowerCase() === 'size')?.name || undefined,
            age_group: this.product.facetValues?.find((fv:any) => fv.facet.code.toLowerCase() === 'age-group')?.name
                || this.selectedVariant.options?.find((o:any) => o.group.code.toLowerCase() === 'age-group')?.name || undefined,
            description: this.product.description || '',
            sku: this.selectedVariant?.sku,
            brand: this.storeInfo?.name ? { "@type": "Brand", "name": this.storeInfo?.name} : undefined,
            offers: {
              "@type": "Offer",
              priceCurrency: 'USD',
              price: this.selectedVariant?.priceWithTax/100,
              availability: "https://schema.org/" + (this.selectedVariant.stockLevel !== 'OUT_OF_STOCK' ? 'InStock' : 'OutOfStock'),
              gtin: this.selectedVariant?.customFields?.gtin || undefined,
              mpn: this.selectedVariant?.customFields?.mpn || undefined,
            },
            aggregateRating: aggregateRating ?? undefined,
            reviews: reviews ?? undefined,
        }
        return data;
    }

    private updateProductMetadata(product: any, variantId: any): void {
        if (!product) return;
        let seoTitle = '';
        let seoDescription = '';
        let ogTitle = '';
        let ogDescription = '';
        let imageUrl = '';
        if(!this.webPresenceConfig || this.webPresenceConfig?.useBuiltInSeo) {
            seoTitle = this.selectedVariant?.name ? this.selectedVariant?.name : product.name;
            seoDescription = product.customFields?.OGDescription ? product.customFields?.OGDescription :
                (product.customFields?.ProductSubtitle ? product.customFields?.ProductSubtitle :
                (stripHtml(product.description, this.platformId)));
            ogTitle = seoTitle;
            ogDescription = seoDescription;
            imageUrl = product.customFields?.OGImage ? this.previewPipe.transform(product.customFields.OGImage, 600, 315) :
                (product.featuredAsset ? (this.previewPipe.transform(product.featuredAsset, 600, 315)) : '');
        } else {
            const titleHtml = this.webPresenceConfig?.title || product.name;
            const descriptionHtml = this.webPresenceConfig.description || '';
            seoTitle = stripHtml(titleHtml, this.platformId);
            seoDescription = stripHtml(descriptionHtml, this.platformId);
            ogTitle = this.webPresenceConfig.openGraphTitleSameAsSeoTitle ? seoTitle :
                (this.webPresenceConfig.openGraphTitle ?
                    stripHtml(this.webPresenceConfig.openGraphTitle, this.platformId) : seoTitle);
            ogDescription = this.webPresenceConfig.openGraphDescriptionSameAsSeoDescription ? seoDescription :
                (this.webPresenceConfig.openGraphDescription ?
                    stripHtml(this.webPresenceConfig.openGraphDescription, this.platformId) : seoDescription);
            imageUrl = this.previewPipe.transform(this.webPresenceConfig?.metaAsset, 600, 315) || '';
        }
        // Set the page title
        this.titleService.setTitle(seoTitle);
        // Set the meta description
        this.metaService.updateTag({ name: 'description', content: seoDescription });
        // Set Open Graph meta tags
        this.metaService.updateTag({ property: 'og:title', content: ogTitle });
        this.metaService.updateTag({ property: 'og:description', content: ogDescription });
        this.metaService.updateTag({ property: 'og:type', content: 'product' });
        this.metaService.updateTag({ property: 'og:url', content: `${this.shopHost}${this.baseUrl}/dp/${product.slug}?variantid=${variantId}` });
        // Set Twitter Card meta tags
        this.metaService.updateTag({ name: 'twitter:title', content: ogTitle });
        this.metaService.updateTag({ name: 'twitter:description', content: ogDescription });
        // Set image meta tags
        if (imageUrl) {
          this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
          this.metaService.updateTag({ property: 'og:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
          this.metaService.updateTag({ property: 'og:image:alt', content: ogTitle });
          this.metaService.updateTag({ name: 'twitter:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
          this.metaService.updateTag({ name: 'twitter:image:alt', content: ogTitle });
        } else {
          this.metaService.updateTag({ name: 'twitter:card', content: 'summary' });
        }
        this.logger.debug(`Updated metadata for product:${seoTitle} variantId:${variantId}`);
    }

    private removeMetadata() {
        this.titleService.setTitle(`${this.storeInfo?.name? this.storeInfo.name : ''}`);
        this.metaService.removeTag('name="description"');
        this.metaService.removeTag('property="og:title"');
        this.metaService.removeTag('property="og:description"');
        this.metaService.removeTag('property="og:type"');
        this.metaService.removeTag('property="og:url"');
        this.metaService.removeTag('property="og:image"');
        this.metaService.removeTag('name="twitter:card"');
        this.metaService.removeTag('name="twitter:title"');
        this.metaService.removeTag('name="twitter:description"');
        this.metaService.removeTag('name="twitter:image"');
    }

    viewCartFromNotification(closeFn: () => void) {
        this.stateService.setState('cartDrawerOpen', true);
        closeFn();
    }

    updateMSRP() {
        if (this.selectedVariant && this.selectedVariant.customFields?.MSRP && this.selectedVariant.customFields.MSRP !== 0) {
            this.selectedVariantMSRP = this.selectedVariant.customFields.MSRP;
            const price = this.selectedVariant.price;
            const msrp = this.selectedVariantMSRP;
            if (msrp && msrp > price) {
                this.discountPercentage = ((msrp - price) / msrp) * 100;
            } else {
                this.discountPercentage = null;
            }
        } else {
            this.selectedVariantMSRP = null;
            this.discountPercentage = null;
        }
    }

    onVariantChange(event: { variant: Variant }) {
        this.logger.debug(`onVariantChange: ${event.variant.id}`);
        this.selectedVariant = event.variant;
        this.inStock = (this.selectedVariant.stockLevel !== 'OUT_OF_STOCK');
        this.updateMSRP();
        this.updateShopNowBannerTopOffset();
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { variantid: this.selectedVariant.id, section: undefined },
            queryParamsHandling: 'merge',
        });
    }

    onTemplateChanged(event: { printTemplate: ProductCustomizationOption | null }) {
        this.selectedPrintTemplate = event.printTemplate;
        this.changeDetector.markForCheck();
    }

    onCustomizationOptionsChanged(options: ProductCustomizationOption[]) {
        this.allCustomizationOptions = options;
        this.changeDetector.markForCheck();
    }

    onMessageValuesChanged(values: { [optionId: string]: string }) {
        this.messageValues = values;
        this.changeDetector.markForCheck();
    }

    onSelectedPaidOptionValuesChanged(values: { [optionId: string]: string }) {
        this.selectedPaidOptionValues = values;
        this.changeDetector.markForCheck();
    }

    onShopNowBannerVisibilityChange(isVisible: boolean) {
        this.showFixedBanner = !isVisible && !this.isMobile;
    }

    updateShopNowBannerTopOffset () {
        let height = 0;
        if (this.isHeaderFloating) {
          height = this.desktopHeaderHeight;
          if (this.withHeaderMessage) {
            height = this.desktopHeaderWithTopMessageHeight;
          }
        }
        if(!this.showTopNav && this.navigationMenu.length > 0) {
            height += this.desktopSectionNavHeight;
        }
        this.shopNowBannerTopOffset = height;
        this.changeDetector.markForCheck();
    }

    private computeH1Heading(): void {
        this.h1PageHeadingIndex = -1;
        this.h1CustomText = '';
        this.h1StyleMode = this.webPresenceConfig?.h1StyleMode || 'auto';
        this.h1CustomStyles = this.computeH1Styles();
        const h1Source = this.webPresenceConfig?.h1Source || 'auto';
        if (h1Source === 'none') {
            return;
        }
        if (h1Source === 'custom') {
            this.h1CustomText = this.webPresenceConfig?.h1Text || '';
            return;
        }
        // 'auto': find first section with a visible title
        // Product pages typically have H1 from product-info already, so only apply to other sections
        if (this.pagecomponentList?.length > 0) {
            this.h1PageHeadingIndex = this.pagecomponentList.findIndex(
                (pc: any) => pc.title || pc.subtitle || pc.content?.title,
            );
        }
    }

    private computeH1Styles(): { [key: string]: string } {
        if (this.h1StyleMode !== 'custom') {
            return {};
        }
        const styles: { [key: string]: string } = {};
        const fontSize = this.isMobile
            ? this.webPresenceConfig?.h1MobileFontSize
            : this.webPresenceConfig?.h1FontSize;
        if (fontSize) {
            styles['font-size'] = `${fontSize}px`;
        }
        if (this.webPresenceConfig?.h1Color) {
            styles['color'] = this.webPresenceConfig.h1Color;
        }
        if (this.webPresenceConfig?.h1Alignment) {
            styles['text-align'] = this.webPresenceConfig.h1Alignment;
        }
        return styles;
    }

    computeLayoutStyles() {
        computeLayoutStyles(this.layoutOrder, this.isMobile);
    }
}
